import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { store } from "Store";
import { useNavigate, useParams } from "react-router-dom";
import { ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { Row, Col, Card, Button } from "react-bootstrap";
import { ICONS } from "Utils/icons";
import { TextButton } from "Components/Button";
import { validateToken } from "./ForgotPasswordAxiosFuncs";
import { PATHS } from "Utils/PagePaths";
import { maskEMail } from "Utils/utilityFunctions";

const ForgotPasswordSelectMFA = () => {

  const { token } = useParams();
  const loginState = store.getState().loginInfo;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  // "SMS" or "EMAIL"
  const [activeMethod, setActiveMethod] = useState("");
  const [mfaFactors, setMfaFactors] = useState({
    mfaStatusEmail: null,
    mfaStatusSms: null,
    smsLast4: null,
    mfaEmail: null
  });

  const handleSelectMethod = (method) => {
    setActiveMethod(method);
  }

  // init load to validate the token in the URL from the email
  const initLoad = async (token) => {
    // the Forgot Password MFA can only be operated when the user is not login!
    if (loginState.token) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.GENERAL_ERROR.title,
          msg: "Please sign out to reset password.",
          showContactHelp: false
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        }
      });
      //stay on the  page
      setIsLoading(true);
      return;
    }

    const res = await validateToken(token)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });

    //console.log("initLoad() forgotpassword select mfa", res);
    if (res && res.data?.status !== 1) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: res.data.globalErrorMessageList,
          showContactHelp: false
        },
        redirect: {
          redirect: true,
          redirectFirst: true,
          path: PATHS.public['Sign In'],
          data: {}
        }
      });
      return;
    }
    if (res && res.data?.status === 1) {

      // mfa is enabled
      // forwardPage:1: Reset Password; 2: Setup MFA;   3: Verify MFA 
      if (res.data.data?.isUserMfaEnabled) {

        // both SMS and Email MFA are enabled 
        if (res.data.data?.mfaStatusEmail === 2 && res.data.data?.mfaStatusSms === 2) {
          // display the page content
          setIsLoading(false);

          // set mfa factors
          setMfaFactors({
            mfaStatusEmail: res.data.data?.mfaStatusEmail,
            mfaStatusSms: res.data.data?.mfaStatusSms,
            smsLast4: res.data.data?.smsLast4,
            mfaEmail: res.data.data?.email
          });
          return;
        }

        // only SMS MFA is enabled: jump to SMS MFA
        if (res.data.data?.mfaStatusSms === 2) {
          navigate("/token/" + token + "/2_1", { state: { isForward: true } });
          return;
        }

        // only Email MFA is enabled: jump to Email MFA
        if (res.data.data?.mfaStatusEmail === 2) {
          navigate("/token/" + token + "/2_2", { state: { isForward: true } });
          return;
        }

        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: "MFA is not setup correctly",
            showContactHelp: false
          },
          redirect: {
            redirect: true,
            redirectFirst: true,
            path: PATHS.public['Sign In'],
            data: {}
          }
        });
        return;
      }

      // mfa is not enabled then jump to the reset password page
      // forwardPage:1: Reset Password; 2: Setup MFA;   3: Verify MFA 
      if (res.data.data?.forwardPage === 1) {
        navigate("/token/" + token + "/3", { state: { isForward: true } });
      }

      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: "MFA is not setup correctly",
          showContactHelp: false
        },
        redirect: {
          redirect: true,
          redirectFirst: true,
          path: PATHS.public['Sign In'],
          data: {}
        }
      });
      return;

    }
  }

  // init
  useEffect(() => {
    initLoad(token);
  }, []);


  const handleNext = (method) => {

    // navigate to SMS MFA
    if (method === "SMS") {
      navigate("/token/" + token + "/2_1", { state: { isForward: true } });
    }

    // navigate to Email MFA
    if (method === "EMAIL") {
      navigate("/token/" + token + "/2_2", { state: { isForward: true } });
    }
  }

  const cancelMfa = () => {
    navigate(PATHS.public['Sign In']);
  }

  return (
    <>
      {isLoading ? null :
        <Row>
          <Col xl={6} lg={6} md={11} sm={12}>
            <Card id="forgotpwd-select-mfa" className="p-4">
              <Card.Body className="text-start">
                <h1 id="mainContent" className="text-start mb-3 blue fw-bold">
                  Two-Factor Authentication
                </h1>
                <hr></hr>
                <p>
                  Please select a verification method.
                </p>
                {/* mfaStatusSms: 2 : enabled */}
                {mfaFactors.mfaStatusSms === 2 ?
                  <>
                    <Row>
                      {/* <Col></Col> */}
                      <Col md={12} sm={12} xl={8}>
                        <Button
                          className={`w-100 ${activeMethod === "SMS" ? 'mfa-method-button-active' : ''}`}
                          onClick={() => handleSelectMethod("SMS")}
                        >
                          <div class="mfa-method">
                            <span class="mfa-icon">{ICONS.sms()}</span>
                            <span class="text-start">
                              <span className="mfa-method-title">SMS</span>
                              <br />
                              <span className="mfa-method-desc">Send an SMS code to ***-***-{mfaFactors.smsLast4}</span>
                            </span>
                          </div>
                        </Button>
                        <hr></hr>
                      </Col>
                      <Col></Col>
                    </Row>
                  </> : null}
                {/* mfaStatusEmail: 2 : enabled */}
                {mfaFactors.mfaStatusEmail === 2 ?
                  <>
                    <Row>
                      {/* <Col></Col> */}
                      <Col md={12} sm={12} xl={8}>
                        <Button
                          className={`w-100 ${activeMethod === "EMAIL" ? 'mfa-method-button-active' : ''}`}
                          onClick={() => handleSelectMethod("EMAIL")}
                        >
                          <div class="mfa-method">
                            <span class="mfa-icon">{ICONS.mailoutlined()}</span>
                            <span class="text-start">
                              <span className="mfa-method-title">Email</span>
                              <br />
                              <span className="mfa-method-desc">Send a code via email to {maskEMail(mfaFactors.mfaEmail)}</span>
                            </span>
                          </div>
                        </Button>
                      </Col>
                      <Col></Col>
                    </Row>
                  </> : null}
                <hr></hr>
                <Row className="mb-3 pt-1">
                  <Col>
                    <TextButton
                      className="w-10 me-3"
                      disabled={activeMethod === ""}
                      variant="normal"
                      onClick={() => handleNext(activeMethod)}
                      content={"Next"}
                    >
                    </TextButton>
                    <TextButton
                      className="w-10"
                      variant="outline"
                      onClick={cancelMfa}
                      content={"Cancel"}
                    >
                    </TextButton>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row >}
    </>
  )

}

export default ForgotPasswordSelectMFA;